import Vue from 'vue';
import {
  extend,
  ValidationObserver,
  setInteractionMode,
  ValidationProvider,
} from 'vee-validate';
import * as Rule from 'vee-validate/dist/rules';

// const PhoneNumber = require('awesome-phonenumber');
import { parsePhoneNumber } from 'awesome-phonenumber';

setInteractionMode('eager');
extend('max', {
  ...Rule.max,
  params: ['length'],
  message: '{_field_} can only have {length} letters',
});
extend('maxNumber', {
  ...Rule.max,
  params: ['length'],
  message: '{_field_} can only have {length} numbers',
});
extend('oneOf', {
  ...Rule.oneOf,
  message: 'At least one item must be selected',
});
extend('email', {
  ...Rule.email,
  message: 'Email must be valid',
});
extend('required', {
  ...Rule.required,
  validate(value) {
    return {
      required: true,
      valid: Array.isArray(value)
        ? value.length > 0
        : [0, '', null, undefined].indexOf(value) === -1,
    };
  },
  message: '{_field_} can not be empty',
  computesRequired: true,
});
extend('min_value', {
  ...Rule.required,
  message: '{_field_} must choose an option',
});
extend('numeric', {
  ...Rule.numeric,
  message: '{_field_} must be numeric',
});
extend('digits', {
  ...Rule.digits,
  message: '{_field_} must be a valid digit',
});
extend('length', {
  ...Rule.length,
  message: 'Invalid {_field_}',
});
extend('min', {
  ...Rule.min,
  message: 'Invalid {_field_}',
});
extend('integer', {
  ...Rule.integer,
  message: 'Invalid {_field_}',
});
extend('url', {
  validate(value) {
    var pattern = new RegExp(
      '^(https?:\\/\\/)?' + // protocol
        '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name
        '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
        '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // port and path
        '(\\?[;&a-z\\d%_.~+=-]*)?' + // query string
        '(\\#[-a-z\\d_]*)?$',
      'i'
    ); // fragment locator
    return pattern.test(value);
  },
  message: 'Please enter a valid url.',
});

extend('phonenumbercustom', {
  getMessage: (field) => `The ${field} is not a valid phone number.`,
  validate: (value) => {
    if (!value) return false;
    // Must be numeric and 10 digits
    if (!/^\d{10}$/.test(value)) return false;
    // Must be valid AU prefix
    if (!/^(02|03|04|07|08)/.test(value)) return false;
    // Reject repeating numbers
    if (/^(\d)\1{9}$/.test(value)) return false;
    // Reject common sequential patterns
    if (['07123456789', '0777777777', '0712345678'].includes(value)) return false;
    // Reject known fake/test numbers
    if (value.startsWith('070000') || value.startsWith('07777') || ['0712345678', '0712345679'].includes(value)) return false;

    try {
      const pn = parsePhoneNumber(value, { regionCode: 'AU' });
      return pn.valid && pn.type.toLowerCase() !== 'mobile';
    } catch (err) {
      return false;
    }
  },
});

extend('phonenumber', {
  getMessage: (field) => `The ${field} is not a valid phone number.`,
  validate: (value) => {
    if (!value) return false;
    // Must be numeric and 10 digits
    if (!/^\d{10}$/.test(value)) return false;
    // Must be valid AU prefix
    if (!/^(02|03|04|07|08)/.test(value)) return false;
    // Reject repeating numbers
    if (/^(\d)\1{9}$/.test(value)) return false;
    // Reject common sequential patterns
    if (['07123456789', '0777777777', '0712345678'].includes(value)) return false;
    // Reject known fake/test numbers
    if (value.startsWith('070000') || value.startsWith('07777') || ['0712345678', '0712345679'].includes(value)) return false;

    try {
      const pn = parsePhoneNumber(value, { regionCode: 'AU' });
      return pn.valid; 
    } catch (err) {
      return false;
    }
  },
});

extend('mobilenumber', {
  getMessage: (field) => `The ${field} is not a valid mobile number.`,
  validate: (value) => {
    if (!value) return false;
    // Must be numeric and 10 digits
    if (!/^\d{10}$/.test(value)) return false;
    // Must be valid AU prefix
    if (!/^(04)/.test(value)) return false;
    // Reject repeating numbers
    if (/^(\d)\1{9}$/.test(value)) return false;
    // Reject common sequential patterns
    if (['04123456789', '0444444444', '0412345678'].includes(value)) return false;
    // Reject known fake/test numbers
    if (value.startsWith('040000') || value.startsWith('04444') || ['0412345678', '0412345679'].includes(value)) return false;

    try {
      const pn = parsePhoneNumber(value, { regionCode: 'AU' });
      return pn.valid;
    } catch (err) {
      return false;
    }
  },
});

extend('inbound', {
  ...Rule.required,
  // Custom validation message
  getMessage: (field) => `The ${field} is not a valid inbound.`,
  // Custom validation rule
  validate: (value) =>
    new Promise((resolve) => {
      // var pn = new PhoneNumber(value, 'AU');
      var pn = parsePhoneNumber(value, { regionCode: 'AU' });
      var type = pn.type.toLowerCase();
      resolve({
        valid: pn.valid && (type == 'toll-free' || type == 'shared-cost'),
      });
    }),
});

extend('dateofbirth', {
  // Custom validation message
  getMessage: (field) => `The ${field} is not a valid birthdate.`,
  // Custom validation rule
  validate: (value) =>
    new Promise((resolve) => {
      // const [day, month, year] = value.substring(0, 10).split('/');
      var age = new Date(new Date() - new Date(value)).getFullYear() - 1970;
      resolve({
        valid: age > 17 && age < 100,
      });
    }),
});

extend('ampt', {
  // Custom validation message
  getMessage: (field) => `The ${field} is not a valid AMPT domain.`,
  // Custom validation rule
  validate: (value) =>
    new Promise((resolve) => {
      resolve({
        valid: value.endsWith('vonex.biz') && value.length < 41,
      });
    }),
});

extend('bsbNumber', {
  // Custom validation message
  getMessage: (field) => `${field} is not a valid.`,
  // Custom validation rule
  validate: (value) => value.length === 7 && !value.startsWith('000'),
});

extend('bankAccountNumber', {
  // Custom validation message
  getMessage: (field) => `${field} is not a valid.`,
  // Custom validation rule
  validate: (value) =>
    new Promise((resolve) => {
      resolve({
        valid: !value.startsWith('0000'),
      });
    }),
});

extend('planId', {
  ...Rule.required,
  // Custom validation message
  getMessage: (field) => `${field} invalid plan id`,
  // Custom validation rule
  validate: (value) => value > 0,
});

extend('serviceId', {
  ...Rule.required,
  // Custom validation message
  getMessage: (field) => `${field} invalid service id`,
  // Custom validation rule
  validate: (value) => value.includes('hr'),
});

extend('contractMonths', {
  ...Rule.required,
  getMessage: (field) => `Invalid contract months`,
  // Custom validation rule
  validate: (value) => value >= 0,
});

extend('alpha_num', {
  ...Rule.alpha_num,
  message: 'Alpha numeric only',
});
extend('locationId', {
  validate: (value) =>
    new Promise((resolve) => {
      const isLocId = /^LOC/;
      resolve({
        valid: isLocId.test(value),
      });
    }),
  message: 'Invalid {_field_}',
});

extend('contactNumber', {
  ...Rule.numeric,
  getMessage: () => `Invalid contact number`,
  validate: (value) => {
    if (!value) return false;
    // Must be exactly 10 digits
    if (!/^\d{10}$/.test(value)) return false;
    // Must start with valid AU prefix
    if (!/^(04|07)/.test(value)) return false;
    // Reject repeating digits (e.g. 0000000000, 1111111111)
    if (/^(\d)\1{9}$/.test(value)) return false;
    // Known fake/test numbers
    const fakeNumbers = new Set([
      '0712345678',
      '0712345679',
      '0777777777',
      '0412345678',
      '0412345679',
      '0444444444',
    ]);
    if (fakeNumbers.has(value) || value.startsWith('070000') || value.startsWith('07777') || value.startsWith('040000') || value.startsWith('04444')) return false;
    
    try {
      const pn = parsePhoneNumber(value, { regionCode: 'AU' });
      if (!pn.valid) return false;
    } catch {
      return false;
    }
    return true;
  },
});

extend('range', {
  ...Rule.required,
  getMessage: (field) => `Invalid range`,
  validate: (value) => value >= 0,
});

extend('between', {
  ...Rule.between,
  message: 'Invalid {_field_}',
});
Vue.component('ValidationObserver', ValidationObserver);
Vue.component('ValidationProvider', ValidationProvider);
